<!-- warehouse_scan/public/js/components/HistoryList.vue -->
<template>
  <div class="history-list">
    <div class="history-header">
      <h4 class="history-title">
        <span class="title-icon">📋</span>
        扫描记录
      </h4>
      <button class="clear-btn" @click="$emit('clear')" title="清空历史">
        <span class="clear-icon">🗑️</span>
        清空
      </button>
    </div>
    
    <div class="history-items">
      <div 
        v-for="(item, index) in history" 
        :key="item.id || index"
        class="history-item"
        :class="{ failed: item.status === 'failed' }"
        @click="$emit('item-click', item)"
      >
        <div class="history-icon" :class="item.type">
          {{ getTypeIcon(item.type) }}
        </div>
        <div class="history-content">
          <div class="history-value" :title="item.value">
            {{ truncate(item.value, 35) }}
          </div>
          <div class="history-meta">
            <span class="history-time">{{ formatTime(item.time) }}</span>
            <span class="history-type">{{ item.type }}</span>
            <span v-if="item.message" class="history-msg" :title="item.message">
              {{ truncate(item.message, 25) }}
            </span>
          </div>
        </div>
        <div class="history-arrow">›</div>
      </div>
      
      <div v-if="history.length === 0" class="empty-history">
        <div class="empty-icon">📭</div>
        <div class="empty-text">暂无扫描记录</div>
        <div class="empty-hint">扫码后将显示在这里</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  history: {
    type: Array,
    default: () => []
  }
});

defineEmits(['clear', 'item-click']);

const getTypeIcon = (type) => {
  const icons = {
    'PALLET': '📦',
    'CASE': '📋',
    'ITEM': '🔖',
    'LOCATION': '📍',
    'SOURCE': '📄',
    'ERROR': '❌',
    'UNKNOWN': '❓'
  };
  return icons[type] || '📌';
};

const formatTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
  
  return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const truncate = (str, len) => {
  if (!str) return '';
  return str.length > len ? str.substring(0, len) + '...' : str;
};
</script>

<style scoped>
.history-list {
  background: white;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f1f3f5;
}

.history-title {
  margin: 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.title-icon {
  font-size: 18px;
}

.clear-btn {
  padding: 5px 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  color: #6c757d;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.clear-btn:hover {
  background: #e9ecef;
  color: #dc3545;
  border-color: #dc3545;
}

.history-items {
  max-height: 350px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
  transition: all 0.2s;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.history-item:hover {
  background: #f8f9fa;
  transform: translateX(4px);
}

.history-item.failed {
  opacity: 0.7;
  background: #fff5f5;
}

.history-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 18px;
  flex-shrink: 0;
}

.history-icon.PALLET {
  background: #e3f2fd;
  color: #1976d2;
}

.history-icon.CASE {
  background: #e8f5e9;
  color: #388e3c;
}

.history-icon.ITEM {
  background: #fff3e0;
  color: #f57c00;
}

.history-icon.LOCATION {
  background: #f3e5f5;
  color: #7b1fa2;
}

.history-icon.SOURCE {
  background: #e1f5fe;
  color: #0288d1;
}

.history-icon.ERROR {
  background: #ffebee;
  color: #c62828;
}

.history-content {
  flex: 1;
  min-width: 0;
}

.history-value {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 11px;
  color: #6c757d;
  align-items: center;
}

.history-time {
  color: #adb5bd;
}

.history-type {
  padding: 2px 6px;
  border-radius: 10px;
  background: #f1f3f5;
}

.history-msg {
  color: #28a745;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-arrow {
  color: #adb5bd;
  font-size: 16px;
  margin-left: 8px;
  opacity: 0;
  transition: opacity 0.2s;
}

.history-item:hover .history-arrow {
  opacity: 1;
}

.empty-history {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-text {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 4px;
}

.empty-hint {
  color: #adb5bd;
  font-size: 12px;
}
</style> 